import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

const Booking = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    package: "",
    year: "",
    make: "",
    model: "",
    date: "",
    time: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("Form submission started with data:", formData);
    
    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.phone || !formData.address || 
          !formData.city || !formData.state || !formData.zip || !formData.package || 
          !formData.year || !formData.make || !formData.model || !formData.date || !formData.time) {
        console.error("Form validation failed - missing required fields");
        toast({
          variant: "destructive",
          title: "Missing Information",
          description: "Please fill out all required fields.",
        });
        return;
      }

      // Combine address fields for database storage
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`;
      console.log("Combined address:", fullAddress);
      
      console.log("Attempting database insertion...");
      
      // Create the data object for insertion
      const appointmentData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: fullAddress,
        package: formData.package,
        vehicle_year: formData.year,
        vehicle_make: formData.make,
        vehicle_model: formData.model,
        preferred_date: formData.date,
        preferred_time: formData.time,
        status: "pending" // Adding a status field
      };
      
      let data;
      let error;
      
      try {
        // First try with Supabase client
        const result = await supabase.from("appointments").insert([appointmentData]).select();
        data = result.data;
        error = result.error;
        
        if (error) {
          console.error("Supabase client insertion failed, trying direct fetch API as fallback...");
          
          // Fallback to direct fetch API if Supabase client fails
          const response = await fetch("https://cltglxlfgzukecingrur.supabase.co/rest/v1/appointments", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNsdGdseGxmZ3p1a2VjaW5ncnVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAzNjg1MjAsImV4cCI6MjA1NTk0NDUyMH0.9B9OjGbKuOYZ6kA4L6UouyrmvRhi44jMHU0IM_ZwxMs",
              "Prefer": "return=representation"
            },
            body: JSON.stringify(appointmentData)
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Direct API call failed: ${JSON.stringify(errorData)}`);
          }
          
          data = await response.json();
          error = null;
          console.log("Direct fetch API insertion successful:", data);
        }
      } catch (insertError) {
        console.error("Both insertion methods failed:", insertError);
        throw new Error(`Error submitting appointment: ${insertError instanceof Error ? insertError.message : String(insertError)}`);
      }

      if (error) {
        console.error("Database insertion error:", error);
        throw new Error(`Error submitting appointment: ${error.message}`);
      }

      console.log("Database insertion successful:", data);

      let customerEmailSent = false;
      let adminEmailSent = false;

      // After successful database insertion, send confirmation emails
      try {
        console.log("Sending customer confirmation email...");
        
        const customerEmailResponse = await supabase.functions.invoke('send-confirmation', {
          body: {
            name: formData.name,
            email: formData.email,
            package: formData.package,
            preferred_date: formData.date,
            preferred_time: formData.time,
          },
        });

        if (customerEmailResponse.error) {
          console.error("Customer email error details:", customerEmailResponse.error);
        } else {
          console.log("Customer confirmation email response:", customerEmailResponse);
          customerEmailSent = true;
        }

        // Send notification email to the admin
        console.log("Sending admin notification email...");
        const adminEmailResponse = await supabase.functions.invoke('send-admin-notification', {
          body: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: fullAddress,
            package: formData.package,
            vehicle_year: formData.year,
            vehicle_make: formData.make,
            vehicle_model: formData.model,
            preferred_date: formData.date,
            preferred_time: formData.time,
          },
        });

        if (adminEmailResponse.error) {
          console.error("Admin email error details:", adminEmailResponse.error);
        } else {
          console.log("Admin notification email response:", adminEmailResponse);
          adminEmailSent = true;
        }

        // Show appropriate toast message based on email results
        if (!customerEmailSent || !adminEmailSent) {
          toast({
            title: "Booking Successful",
            description: "Your booking was received but there was an issue sending notification emails.",
          });
        } else {
          toast({
            title: "Booking Successful",
            description: "Your booking was received and confirmation emails have been sent.",
          });
        }
      } catch (emailError) {
        console.error("Email sending error:", emailError);
        // Still show success since the booking was stored in the database
        toast({
          title: "Booking Successful",
          description: "Your booking was received but there was an issue sending notification emails.",
        });
      }

      // Reset the form
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        package: "",
        year: "",
        make: "",
        model: "",
        date: "",
        time: "",
      });

      console.log("Form reset, navigating to confirmation page...");
      // Redirect to confirmation page regardless of email status
      navigate("/booking/confirmation");
    } catch (error) {
      console.error("Error in booking process:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "There was a problem submitting your appointment. Please try again.",
      });
    }
  };

  const packages = [
    {
      id: "eco-wash",
      name: "ECO Wash Package",
      description: "Waterless Exterior Hand Wash, Interior Vacuum, Dashboard Wipe",
    },
    {
      id: "complete-detail",
      name: "Complete Detail Package",
      description: "Complete Interior & Exterior Detail with Polish",
    },
    {
      id: "interior-detail",
      name: "Interior Detail Package",
      description: "Full Interior Cleaning with Steam and Odor Elimination",
    },
    {
      id: "eco-wax",
      name: "ECO Wax Package",
      description: "ECO Wash with Hand-Applied Premium Wax",
    },
    {
      id: "exterior-detailing",
      name: "Exterior Detailing Package",
      description: "Hand Polish with Premium Compounds and Paint Decontamination",
    },
    {
      id: "ceramic-coatings",
      name: "Ceramic Coatings",
      description: "Professional-Grade Ceramic Coating with 3-5 Years Protection",
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 bg-secondary">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-8 text-primary-dark">Book Your Detail</h1>
        <Card className="max-w-2xl mx-auto border border-primary/20 shadow-lg">
          <CardHeader className="bg-primary/5">
            <CardTitle className="text-primary">Schedule Your Mobile Detailing Service</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700">Full Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="border-gray-300 focus:border-primary focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              {/* Service Address Section */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-800">Service Address</h3>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-gray-700">Street Address</Label>
                  <Input
                    id="address"
                    placeholder="123 Main St"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-gray-700">City</Label>
                    <Input
                      id="city"
                      placeholder="Minneapolis"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                      required
                      className="border-gray-300 focus:border-primary focus:ring-primary"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-gray-700">State</Label>
                    <Input
                      id="state"
                      placeholder="MN"
                      value={formData.state}
                      onChange={(e) =>
                        setFormData({ ...formData, state: e.target.value })
                      }
                      required
                      maxLength={2}
                      className="border-gray-300 focus:border-primary focus:ring-primary"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="zip" className="text-gray-700">ZIP Code</Label>
                  <Input
                    id="zip"
                    placeholder="55403"
                    value={formData.zip}
                    onChange={(e) =>
                      setFormData({ ...formData, zip: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="package" className="text-gray-700">Select Package</Label>
                <Select
                  value={formData.package}
                  onValueChange={(value) =>
                    setFormData({ ...formData, package: value })
                  }
                >
                  <SelectTrigger className="border-gray-300 focus:border-primary focus:ring-primary">
                    <SelectValue placeholder="Choose a package" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id} className="cursor-pointer hover:bg-accent hover:text-accent-foreground">
                        <div>
                          <div className="font-medium">{pkg.name}</div>
                          <div className="text-xs text-gray-500">{pkg.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="year" className="text-gray-700">Vehicle Year</Label>
                  <Input
                    id="year"
                    type="number"
                    placeholder="2024"
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({ ...formData, year: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="make" className="text-gray-700">Vehicle Make</Label>
                  <Input
                    id="make"
                    placeholder="Toyota"
                    value={formData.make}
                    onChange={(e) =>
                      setFormData({ ...formData, make: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model" className="text-gray-700">Vehicle Model</Label>
                  <Input
                    id="model"
                    placeholder="Camry"
                    value={formData.model}
                    onChange={(e) =>
                      setFormData({ ...formData, model: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-gray-700">Preferred Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time" className="text-gray-700">Preferred Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) =>
                      setFormData({ ...formData, time: e.target.value })
                    }
                    required
                    className="border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary-dark text-white">
                Book Now
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Booking;
